import React from 'react';
import dayjs from 'dayjs';
import { Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { WorkProgressBagMawbBagParcelSummaryRow } from '../../graphql/queries/getWorkProgressBagMawbBagParcelSummaries';
import { BIG_BAG_TYPE_WITH_MAWB, type BigBagDrilldownBagType, type BigBagType } from '../bagType';
import { BigBagDrilldownTitleSource } from '../bigBagDrilldownTitleSource';
import { BigBagParcelDiffRateCell, CLICKABLE_UNDERLINE, bigBagDiffRateTone, bigBagStatusKey } from '../bigBagHelpers';

/**
 * 包裹差异 tab 第三级表：MAWB 下按大包号 + 日期汇总的包裹差异行。
 * 列：大包号(可下钻) / 扫描日期 / 预期包裹 / 入库包裹 / 差异包裹(可下钻) / 差异率 / 状态。
 *
 * 与下钻 drawer 同源：`scanTime` 来自 client_carton.scan_time（在 Application 层 merge）。
 */
export interface MawbBagParcelSummaryTableProps {
  customerId: number;
  customerName: string;
  mawb: string;
  rows: WorkProgressBagMawbBagParcelSummaryRow[];
  loading: boolean;
  onBagDrilldown: (
    ctx: {
      customerId: number;
      customerName: string;
      bagType: BigBagType;
      mawb?: string | null;
      bigBagNo?: string | null;
      detailArrivalDate?: string | null;
    },
    opts: {
      titleSource: BigBagDrilldownTitleSource;
      nextIsInbound?: true | null;
    },
  ) => void;
  onParcelDrilldown: (
    ctx: {
      customerId: number;
      customerName: string;
      bagType: BigBagDrilldownBagType;
      mawb?: string | null;
      bagNo?: string | null;
    },
    titleSource: BigBagDrilldownTitleSource,
  ) => void;
}

const MawbBagParcelSummaryTable: React.FC<MawbBagParcelSummaryTableProps> = ({
  customerId,
  customerName,
  mawb,
  rows,
  loading,
  onBagDrilldown,
  onParcelDrilldown,
}) => {
  const { t } = useTranslation();
  const statusLabel = (key: ReturnType<typeof bigBagStatusKey>) => t(`wms.progress.bigBag.status.${key}`);

  return (
    <Table
      rowKey={(r) => `${r.bagNumber}-${r.arrivalDate}`}
      size="middle"
      pagination={false}
      loading={loading}
      dataSource={rows}
      // 第三级行数多时表体内滚动；不用 sticky，避免内嵌表头遮住第一行。
      scroll={{ x: 1100, y: 360 }}
      columns={[
        {
          title: t('wms.progress.bigBag.columns.bagNumber'),
          dataIndex: 'bagNumber',
          key: 'bagNumber',
          width: 200,
          render: (v: string, r: WorkProgressBagMawbBagParcelSummaryRow) => {
            const scanDay = r.scanTime ? dayjs.utc(r.scanTime).local().format('YYYY-MM-DD') : null;
            return (
              <span
                className={`text-violet-600 ${CLICKABLE_UNDERLINE}`}
                onClick={() =>
                  onBagDrilldown(
                    {
                      customerId,
                      customerName,
                      bagType: BIG_BAG_TYPE_WITH_MAWB,
                      mawb,
                      bigBagNo: v,
                      detailArrivalDate: scanDay,
                    },
                    {
                      titleSource: r.isInbound
                        ? BigBagDrilldownTitleSource.MawbBagDetailInbound
                        : BigBagDrilldownTitleSource.MawbBagDetailNotInbound,
                      nextIsInbound: r.isInbound ? true : null,
                    },
                  )
                }
              >
                {v}
              </span>
            );
          },
        },
        {
          title: t('wms.progress.bigBag.columns.bagScanDate'),
          key: 'bagScanDate',
          width: 160,
          render: (_: unknown, r: WorkProgressBagMawbBagParcelSummaryRow) => {
            const scanDay = r.scanTime ? dayjs.utc(r.scanTime).local().format('YYYY-MM-DD') : '';
            return <span className="tabular-nums text-gray-700">{scanDay || '-'}</span>;
          },
        },
        {
          title: t('wms.progress.bigBag.columns.expectedParcelCount'),
          dataIndex: 'expectedParcelCountSum',
          key: 'expectedParcelCountSum',
          width: 120,
          render: (v: number) => <span className="tabular-nums">{Number(v ?? 0).toLocaleString()}</span>,
        },
        {
          title: t('wms.progress.bigBag.columns.inboundParcelCount'),
          dataIndex: 'inboundParcelCountSum',
          key: 'inboundParcelCountSum',
          width: 120,
          render: (v: number) => <span className="tabular-nums">{Number(v ?? 0).toLocaleString()}</span>,
        },
        {
          title: t('wms.progress.bigBag.columns.parcelDiff'),
          dataIndex: 'parcelDiffSum',
          key: 'parcelDiffSum',
          width: 120,
          render: (v: number, r: WorkProgressBagMawbBagParcelSummaryRow) => {
            const n = Number(v ?? 0);
            if (n === 0) return <span className="tabular-nums">{n.toLocaleString()}</span>;
            return (
              <span
                role="button"
                tabIndex={0}
                className={`tabular-nums ${CLICKABLE_UNDERLINE}`}
                onClick={() =>
                  onParcelDrilldown(
                    {
                      customerId,
                      customerName,
                      bagType: BIG_BAG_TYPE_WITH_MAWB,
                      mawb,
                      bagNo: r.bagNumber,
                    },
                    BigBagDrilldownTitleSource.MawbBagParcelDiffNotInbound,
                  )
                }
              >
                {n.toLocaleString()}
              </span>
            );
          },
        },
        {
          title: t('wms.progress.bigBag.columns.parcelDiffRate'),
          dataIndex: 'parcelDiffRate',
          key: 'parcelDiffRate',
          width: 220,
          render: (v: number) => <BigBagParcelDiffRateCell rate={Number(v ?? 0)} />,
        },
        {
          title: t('wms.progress.bigBag.columns.status'),
          key: 'status',
          width: 100,
          render: (_: unknown, r: WorkProgressBagMawbBagParcelSummaryRow) => {
            const rate = Number(r.parcelDiffRate ?? 0);
            const { tagColor } = bigBagDiffRateTone(rate);
            return (
              <Tag className="ml-1" color={tagColor}>
                {statusLabel(bigBagStatusKey(rate))}
              </Tag>
            );
          },
        },
      ]}
      className="[&_.ant-table-container]:bg-transparent [&_.ant-table-content]:bg-transparent [&_.ant-table-tbody>tr>td]:bg-transparent [&_.ant-table-thead>tr>th]:!bg-[#fafafa] [&_.ant-table-sticky-holder]:!bg-[#fafafa]"
    />
  );
};

export default MawbBagParcelSummaryTable;
